// Category Trending UI panel.
// Shows genre breakdown of a chart as horizontal percentage bars.
// Clicking a genre row highlights matching app cards in the Chart Browser.

import { t, getLocale } from '../i18n/index.js';
import { computeCategoryBreakdown, summarizeCategoryConcentration } from '../domain/categoryTrending.js';
import { genreName } from '../data/genres.js';

/** @param {string} str */
function escHtml(str) {
  return (str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Render a Category Trending panel into `container`.
 *
 * @param {HTMLElement} container
 * @param {Array<{primaryGenre?: string|null, genreId?: string|null}>} entries
 * @param {(genreId: string|null) => void} [onSelectGenre] - Optional callback when user clicks a genre row.
 */
export function renderCategoryTrending(container, entries, onSelectGenre) {
  if (!container) return;

  const breakdown = computeCategoryBreakdown(entries);
  if (breakdown.length === 0) {
    container.hidden = true;
    container.innerHTML = '';
    return;
  }

  const locale = getLocale();
  const summary = summarizeCategoryConcentration(breakdown);
  const totalCount = breakdown.reduce((sum, item) => sum + item.count, 0);
  const topBreakdown = breakdown.find((item) => item.genre === summary.topGenre) ?? breakdown[0];
  const summaryGenre = genreName(topBreakdown.genreId, locale, summary.topGenre ?? topBreakdown.genre);
  const summaryText = t('categoryTrending.summary', {
    genre: summaryGenre,
    topCount: String(summary.topCount),
    totalCount: String(totalCount),
    topPct: String(summary.topPct),
    distinctGenres: String(summary.distinctGenres),
  });
  const rows = breakdown
    .map(({ genre, genreId, count, pct }) => {
      // Localize by stable genre id; fall back to the English name the feed gave us.
      const label = genreName(genreId, locale, genre);
      const ariaLabel = t('categoryTrending.rowAriaLabel', {
        genre: label,
        count: String(count),
        totalCount: String(totalCount),
        pct: String(pct),
      });
      const clickable = onSelectGenre ? ' role="button" tabindex="0"' : '';
      return `
        <li
          class="category-trending__row"
          data-genre-id="${escHtml(genreId ?? '')}"
          data-genre="${escHtml(genre)}"
          aria-label="${escHtml(ariaLabel)}"
          ${clickable}
        >
          <span class="category-trending__row-main">
            <span class="category-trending__genre">${escHtml(label)}</span>
            <span class="category-trending__stats">
              <span class="category-trending__count">
                <span class="visually-hidden">${escHtml(t('categoryTrending.countLabel'))}: </span>${count}
              </span>
              <span class="category-trending__pct">
                <span class="visually-hidden">${escHtml(t('categoryTrending.percentLabel'))}: </span>${pct}%
              </span>
            </span>
          </span>
          <span class="category-trending__bar-track" role="img" aria-label="${escHtml(ariaLabel)}">
            <span class="category-trending__bar-fill" style="width: ${pct}%"></span>
          </span>
        </li>
      `;
    })
    .join('');

  container.innerHTML = `
    <div class="category-trending" role="region" aria-label="${escHtml(t('categoryTrending.title'))}">
      <h3 class="category-trending__title">${escHtml(t('categoryTrending.title'))}</h3>
      <p class="category-trending__summary">${escHtml(summaryText)}</p>
      <ul class="category-trending__list" role="list">${rows}</ul>
    </div>
  `;
  container.hidden = false;

  if (onSelectGenre) {
    container.querySelectorAll('.category-trending__row').forEach((row) => {
      const activate = () => {
        const gid = /** @type {HTMLElement} */ (row).dataset.genreId || null;
        container.querySelectorAll('.category-trending__row').forEach((r) =>
          r.classList.remove('category-trending__row--active'),
        );
        row.classList.add('category-trending__row--active');
        onSelectGenre(gid);
      };
      row.addEventListener('click', activate);
      row.addEventListener('keydown', (e) => {
        if (/** @type {KeyboardEvent} */ (e).key === 'Enter' || /** @type {KeyboardEvent} */ (e).key === ' ') {
          e.preventDefault();
          activate();
        }
      });
    });
  }
}

/**
 * Hide and clear the category trending panel.
 * @param {HTMLElement|null} container
 */
export function hideCategoryTrending(container) {
  if (!container) return;
  container.hidden = true;
  container.innerHTML = '';
}
